import React from "react";
import { Alert } from "antd";
import moment from "moment";
import InfiniteScroll from "react-infinite-scroller";
import { useTranslation } from "react-i18next";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

// components
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelopeOpen,
  faEnvelope,
} from "@fortawesome/free-regular-svg-icons";
import { CenteredLoading } from "common/loading";

// graphql
import { useMutation } from "@apollo/client";
import GET_NOTIFICATION from "graphql/queries/GetNotifications";
import MARK_NOTIFICATIONS_READ from "graphql/mutations/MarkNotificationsRead";
import GET_NAVBAR_INFO from "graphql/queries/GetNavbarInfo";

import { Notification } from "types";
import useInfiniteQuery from "hooks/useInfiniteQuery";
import * as Styled from "./MyMailboxStyles";

const { FixedHeightContainer } = Styled;

const MyMailbox = () => {
  const { t } = useTranslation("my_mailbox");

  const {
    nodes: notifications,
    pageInfo,
    loading,
    error,
    loadMore,
  } = useInfiniteQuery("notifications", GET_NOTIFICATION);

  const [markNotificationsRead] = useMutation(MARK_NOTIFICATIONS_READ, {
    refetchQueries: [{ query: GET_NAVBAR_INFO }],
  });

  if (loading) return null;
  if (error)
    return (
      <Alert
        message={t("Error loading mailbox. Please try again.")}
        type="error"
      />
    );

  const renderNotificationIcon = (readAt: string | null) =>
    readAt ? (
      <FontAwesomeIcon icon={faEnvelopeOpen as IconProp} />
    ) : (
      <FontAwesomeIcon icon={faEnvelope as IconProp} />
    );

  const genExtra = (notification: Notification) => (
    <>
      <span>
        {moment(notification.createdAt, "YYYY-MM-DD hh:mm:ss UTC").format("ll")}
      </span>
      <span>&nbsp;</span>
      {renderNotificationIcon(notification.readAt)}
    </>
  );

  const handleFontWeight = (notification: Notification) => {
    if (!notification.readAt) {
      return "bold";
    }
    return "normal";
  };

  const renderNotification = (notification: Notification) => (
    <Styled.Panel
      header={notification.title}
      key={notification.id}
      extra={genExtra(notification)}
      style={{ fontWeight: handleFontWeight(notification) }}
    >
      <p>{notification.content}</p>
      <a href={notification.actionUrl} target="blank" rel="noopener noreferrer">
        {notification.actionText}
      </a>
    </Styled.Panel>
  );

  if (!notifications || notifications.length === 0) {
    return <p style={{ textAlign: "center" }}>{t("Your mailbox is empty.")}</p>;
  }

  const openedNotificationIds = notifications
    .filter((notification: Notification) => notification.readAt)
    .map((notification: Notification) => notification.id);

  const handleToggling = (openedIds: string | string[]) => {
    const ids = [openedIds].flat();
    markNotificationsRead({
      variables: {
        input: { ids },
      },
    });
  };

  return (
    <FixedHeightContainer>
      <InfiniteScroll
        pageStart={0}
        loadMore={loadMore}
        hasMore={pageInfo?.hasNextPage}
        loader={<CenteredLoading key={0} />}
        useWindow={false}
      >
        <Styled.Collapse
          defaultActiveKey={openedNotificationIds}
          onChange={handleToggling}
        >
          {notifications.map((notification: Notification) =>
            renderNotification(notification)
          )}
        </Styled.Collapse>
      </InfiniteScroll>
    </FixedHeightContainer>
  );
};

export default MyMailbox;
